import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminDashboardAPI, activityAPI } from '../../services/api';
import StatCard from './shared/StatCard';
import QuickActionButton from './shared/QuickActionButton';
import CompletionRateChart from './shared/CompletionRateChart';
import PPDDashboard from './PPDDashboard';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selectedActivityId, setSelectedActivityId] = useState(null);

  // Drill-down state
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  // Load activities on mount
  useEffect(() => {
    loadActivities();
  }, []);

  // Reload dashboard when activity selection changes
  useEffect(() => {
    if (activities.length > 0) {
      loadDashboardData();
    }
  }, [selectedActivityId, activities.length]);

  const loadActivities = async () => {
    try {
      const response = await activityAPI.getActivities();
      if (response.success) {
        // API returns paginated result: { data: { data: [...], page, totalCount } }
        const activitiesArray = response.data.data || [];
        setActivities(activitiesArray);
        // Set default to active activity (if available)
        const activeActivity = activitiesArray.find(a => a.status?.toUpperCase() === 'ACTIVE');
        if (activeActivity) {
          setSelectedActivityId(activeActivity.activityId);
        }
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminDashboardAPI.getAdminDashboard(selectedActivityId);

      if (response.success) {
        setDashboardData(response.data);
      } else {
        setError(response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Failed to load Admin dashboard data:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-MY');
  };

  const handleViewDistrict = (district) => {
    setSelectedDistrict(district);
  };

  const handleBackToOverview = () => {
    setSelectedDistrict(null);
  };

  // If a district is selected, show PPD Dashboard for that district
  if (selectedDistrict) {
    return (
      <PPDDashboard
        ppdCode={selectedDistrict.ppdCode}
        activityId={selectedActivityId}
        onBack={handleBackToOverview}
      />
    );
  }

  if (loading) {
    return (
      <div className="dashboard-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button onClick={loadDashboardData} className="btn btn-primary">
          <i className="fas fa-redo"></i> Retry
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-error">
        <i className="fas fa-info-circle"></i>
        <p>No dashboard data available</p>
      </div>
    );
  }

  const { overview, districts } = dashboardData;

  return (
    <div className="admin-dashboard">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>System Dashboard - Administrator</h2>
        <div className="header-buttons">
          <button onClick={loadDashboardData} className="header-btn btn-refresh" title="Refresh Dashboard">
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      {/* Activity Selector Row */}
      <div className="top-section">
        <div className="left-info">
          <p><strong>PENGGUNA:</strong> {user?.name || user?.email}</p>
          <p><strong>JAWATAN:</strong> System Administrator</p>
        </div>
        <div className="right-info">
          <p>
            <strong>ACTIVITY:</strong>{' '}
            <select
              id="activity-select"
              value={selectedActivityId || ''}
              onChange={(e) => setSelectedActivityId(e.target.value ? parseInt(e.target.value) : null)}
              className="activity-dropdown"
            >
              {activities.map((activity) => (
                <option key={activity.activityId} value={activity.activityId}>
                  {activity.activityName || `Activity ${activity.enrollYear}`}
                  {activity.status?.toUpperCase() === 'ACTIVE' ? ' (Active)' : ''}
                </option>
              ))}
            </select>
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          icon="fa-map-marked-alt"
          value={overview.totalPPDs}
          label="Total Districts"
          color="purple"
        />
        <StatCard
          icon="fa-school"
          value={overview.totalSchools.toLocaleString()}
          label="Total Schools"
          color="primary"
        />
        <StatCard
          icon="fa-users"
          value={overview.totalStudents.toLocaleString()}
          label="Total Students"
          color="success"
        />
        <StatCard
          icon="fa-exclamation-circle"
          value={overview.openIssues}
          label="Open Issues"
          color={overview.openIssues > 0 ? "warning" : "info"}
        />
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h3><i className="fas fa-bolt"></i> Quick Actions</h3>
        <div className="quick-actions-grid">
          <QuickActionButton
            icon="fa-chart-line"
            label="Report Summary"
            path="/report-summary"
            color="primary"
          />
          <QuickActionButton
            icon="fa-school"
            label="School Reports"
            path="/report-school"
            color="info"
          />
          <QuickActionButton
            icon="fa-users"
            label="Student Listing"
            path="/report-student-listing"
            color="success"
          />
          <QuickActionButton
            icon="fa-exclamation-circle"
            label="Issue Management"
            path="/issue-reports"
            color="warning"
          />
        </div>
        <div className="download-buttons-row">
          <a className="download-icon-btn download-pdf" href="/docs/Sukatan Pakaian dan Kasut 2026 v2.pdf" download title="Sukatan Pakaian & Kasut 2026">
            <i className="fas fa-file-pdf"></i>
          </a>
          <a className="download-icon-btn download-excel" href="/docs/KTC-Student-Input-Form-Template 2026.xlsx" download title="Borang Input Pelajar 2026">
            <i className="fas fa-file-excel"></i>
          </a>
          <a className="download-icon-btn download-word" href="/docs/KTC System(PPD).docx" download title="Manual Pengguna (PPD)">
            <i className="fas fa-file-word"></i>
          </a>
          <a className="download-icon-btn download-word" href="/docs/KTC SYSTEM(Headmaster).docx" download title="Manual Pengguna (Guru Besar)">
            <i className="fas fa-file-word"></i>
          </a>
          <a className="download-icon-btn download-word" href="/docs/KTC System(KTC-Admin).docx" download title="Manual Pengguna (KTC Admin)">
            <i className="fas fa-file-word"></i>
          </a>
        </div>
      </div>

      {/* Overall Progress Section */}
      {overview.activityName && (
        <div className="dashboard-section activity-progress-section">
          <h3><i className="fas fa-tasks"></i> Overall Progress: {overview.activityName}</h3>
          <div className="activity-progress-card">
            <div className="progress-header">
              <span className="progress-label">System-Wide Completion Rate</span>
              <span className="progress-percentage">{overview.overallCompletionRate}%</span>
            </div>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${overview.overallCompletionRate}%` }}
              ></div>
            </div>
            <div className="progress-details">
              <span>Across {overview.totalPPDs} districts</span>
              <span className="separator">•</span>
              <span>{overview.totalSchools} schools</span>
              <span className="separator">•</span>
              <span>{overview.totalStudents.toLocaleString()} students</span>
            </div>
          </div>
        </div>
      )}

      {/* Completion Rate Chart by District */}
      {districts.length > 0 && (
        <CompletionRateChart
          data={districts.map(d => ({
            code: d.ppdCode,
            completed: d.completedSchools || 0,
            inProgress: d.inProgressSchools || 0,
            notStarted: d.notStartedSchools || 0,
            total: d.totalSchools || 0
          }))}
          title="School Completion Rate by District"
          xAxisLabel="District"
        />
      )}

      <div className="dashboard-content">
        {/* Districts Summary Table */}
        <div className="dashboard-section full-width">
          <h3><i className="fas fa-map-marker-alt"></i> Districts Summary ({districts.length} districts)</h3>
          <div className="districts-table-container">
            <table className="districts-table">
              <thead>
                <tr>
                  <th>District Code</th>
                  <th>District Name</th>
                  <th className="text-center">Schools</th>
                  <th className="text-center">Students</th>
                  <th className="text-center">Completion</th>
                  <th className="text-center">Issues</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {districts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">No districts found</td>
                  </tr>
                ) : (
                  districts.map((district) => (
                    <tr key={district.ppdCode}>
                      <td className="district-code">{district.ppdCode}</td>
                      <td className="district-name">{district.ppdName}</td>
                      <td className="text-center">{district.totalSchools}</td>
                      <td className="text-center">{district.totalStudents.toLocaleString()}</td>
                      <td className="text-center">
                        <div className="completion-cell">
                          <div className="mini-progress-bar">
                            <div
                              className="mini-progress-fill"
                              style={{ width: `${district.completionRate}%` }}
                            ></div>
                          </div>
                          <span className="completion-text">
                            {district.closedTahun}/{district.totalTahun} Closed ({district.completionRate}%)
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        {district.openIssuesCount > 0 ? (
                          <span className="badge badge-warning">{district.openIssuesCount}</span>
                        ) : (
                          <span className="text-muted">0</span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn-view-details"
                          onClick={() => handleViewDistrict(district)}
                          title={`View details for ${district.ppdCode}`}
                        >
                          <i className="fas fa-eye"></i> View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* System Management */}
        <div className="dashboard-section">
          <h3><i className="fas fa-cogs"></i> System Management</h3>
          <div className="quick-actions-grid">
            <QuickActionButton
              icon="fa-user-cog"
              label="User Management"
              path="/user-management"
              color="purple"
            />
            <QuickActionButton
              icon="fa-shield-alt"
              label="Authorization"
              path="/authorization-management"
              color="danger"
            />
            <QuickActionButton
              icon="fa-calendar-alt"
              label="Activity Management"
              path="/activity-management"
              color="info"
            />
            <QuickActionButton
              icon="fa-history"
              label="Audit Logs"
              path="/system-audit-log"
              color="warning"
            />
          </div>
        </div>

      </div>

      {/* Cache Info Footer */}
      <div className="dashboard-footer">
        <small className="text-muted">
          Last updated: {formatDateTime(dashboardData.cacheTimestamp)}
        </small>
      </div>
    </div>
  );
};

export default AdminDashboard;
